import React, { useState } from 'react';
import { Menu, X, User, Settings, Shield, LogOut } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';

interface MobileHeaderProps {
  onMenuToggle?: () => void;
  isSidebarOpen?: boolean;
}

const MobileHeader: React.FC<MobileHeaderProps> = ({
  onMenuToggle,
  isSidebarOpen = false
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [showAccountMenu, setShowAccountMenu] = useState(false);

  const handleNavigate = (href: string) => {
    setShowAccountMenu(false);
    navigate(href);
  };

  const handleLogout = () => {
    setShowAccountMenu(false);
    logout();
  };

  return (
    <header
      className="lg:hidden fixed top-0 left-0 right-0 z-50 backdrop-blur-lg safe-area-top"
      style={{
        backgroundColor: 'rgba(244,245,247,0.94)',
        borderBottom: '1px solid #EBEDF1',
      }}
    >
      <div className="flex items-center justify-between px-3 sm:px-4 py-2.5 sm:py-3">
        {/* Hamburger — flex centrato + dimensioni esplicite per centratura precisa */}
        <button
          onClick={onMenuToggle}
          className="inline-flex items-center justify-center w-10 h-10 sm:w-11 sm:h-11 rounded-xl leading-none p-0 active:scale-95 transition-all duration-200 touch-manipulation"
          style={{
            backgroundColor: '#FFFFFF',
            border: '1px solid #DDE0E6',
            color: '#13161C',
          }}
          aria-label={isSidebarOpen ? 'Chiudi menu' : 'Apri menu'}
          aria-expanded={isSidebarOpen}
        >
          {isSidebarOpen ? (
            <X className="w-5 h-5 sm:w-[22px] sm:h-[22px] block" strokeWidth={2} />
          ) : (
            <Menu className="w-5 h-5 sm:w-[22px] sm:h-[22px] block" strokeWidth={2} />
          )}
        </button>

        {/* Wordmark BYMARKE (tinto inchiostro) centrato */}
        <div className="absolute left-1/2 transform -translate-x-1/2">
          <img
            src="/bymarke-wordmark-white.png"
            alt="ByMarke"
            className="h-4 sm:h-[18px] w-auto select-none"
            style={{ filter: 'brightness(0)' }}
            draggable={false}
          />
        </div>

        {/* Account — menu a tendina in alto a destra */}
        <button
          onClick={() => setShowAccountMenu(v => !v)}
          className="inline-flex items-center justify-center w-10 h-10 sm:w-11 sm:h-11 rounded-xl leading-none p-0 active:scale-95 transition-all duration-200 touch-manipulation"
          style={{
            backgroundColor: '#FFFFFF',
            border: '1px solid #DDE0E6',
            color: '#13161C',
          }}
          aria-label="Account"
          aria-expanded={showAccountMenu}
        >
          <User className="w-5 h-5 sm:w-[22px] sm:h-[22px] block" strokeWidth={2} />
        </button>
      </div>

      {/* Menu Account */}
      <AnimatePresence>
        {showAccountMenu && (
          <>
            {/* Backdrop per chiusura al tap esterno */}
            <div
              className="fixed inset-0 z-40"
              onClick={() => setShowAccountMenu(false)}
              aria-hidden="true"
            />

            <motion.div
              className="absolute right-2 sm:right-3 top-[calc(100%+6px)] z-50 w-[260px] bg-white dark:bg-gray-800 rounded-[10px] shadow-lg border border-slate-200 dark:border-gray-700 overflow-hidden"
              initial={{ opacity: 0, y: -8, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -8, scale: 0.96 }}
              transition={{ duration: 0.18 }}
            >
              <div className="p-2">
                <div className="text-xs font-medium text-slate-500 dark:text-slate-400 px-3 py-2">
                  Account
                </div>

                {/* User Info */}
                <div className="flex items-center gap-3 px-3 py-2 mb-1">
                  <div className="w-10 h-10 rounded-md bg-slate-50 border border-slate-200 flex items-center justify-center">
                    <User className="w-5 h-5 text-slate-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-slate-900 dark:text-slate-100 truncate">{user?.username}</div>
                    <div className="text-xs text-slate-500 dark:text-slate-400 capitalize">{user?.role?.replace('_', ' ')}</div>
                  </div>
                </div>

                {/* Profilo */}
                <button
                  onClick={() => handleNavigate('/profile')}
                  className={`w-full flex items-center gap-3 px-3 py-3 rounded-md transition-colors touch-manipulation active:scale-[0.98] ${
                    location.pathname === '/profile'
                      ? 'bg-slate-100 text-slate-900 font-medium'
                      : 'text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-gray-700'
                  }`}
                >
                  <Settings className="w-5 h-5 text-slate-500 dark:text-slate-400" />
                  <span>Il Mio Profilo</span>
                </button>

                {/* Admin */}
                {user?.role === 'admin' && (
                  <button
                    onClick={() => handleNavigate('/admin-assignments')}
                    className={`w-full flex items-center gap-3 px-3 py-3 rounded-md transition-colors touch-manipulation active:scale-[0.98] ${
                      location.pathname === '/admin-assignments'
                        ? 'bg-slate-100 text-slate-900 font-medium'
                        : 'text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-gray-700'
                    }`}
                  >
                    <Shield className="w-5 h-5 text-slate-500 dark:text-slate-400" />
                    <span>Gestione Assegnazioni</span>
                  </button>
                )}

                {/* Logout */}
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-3 px-3 py-3 rounded-xl text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors touch-manipulation active:scale-[0.98]"
                >
                  <LogOut className="w-5 h-5" />
                  <span>Esci</span>
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </header>
  );
};

export default MobileHeader;
